import { type FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify/unstyled';

import { urls } from '@app/urls';
import {
  type UpdatePasswordFormValues,
  UpdatePasswordSchema,
} from '@features/authentication/components/modals/schemas/update-password.schema';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/ui';
import { GenericButton, PasswordInput } from '@shared/components/ui/custom';
import { OiWordmarkLogin } from '@shared/icons';
import type { RTKQueryError } from '@shared/utils/api.types';

import { useSavePasswordMutation } from '../api/authentication.api';

export const UpdatePasswordPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [savePassword, { isLoading }] = useSavePasswordMutation();

  const form = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(UpdatePasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: UpdatePasswordFormValues) => {
    try {
      await savePassword({
        token,
        newPassword: data.password,
      }).unwrap();

      toast.success('Password update successful. You can log in with your new password.');
      void navigate(urls.login.path);
    } catch (err) {
      const errorObj = err as RTKQueryError;
      const backendErrors = errorObj?.data?.errors ?? {};

      if (Object.keys(backendErrors).length > 0) {
        Object.entries(backendErrors).forEach(([key, message]) => {
          const fieldName = key === 'newPassword' ? 'password' : key;
          form.setError(fieldName as keyof UpdatePasswordFormValues, {
            type: 'server',
            message: message,
          });
        });
      } else {
        const globalMessage = errorObj?.message ?? 'Error updating password.';
        toast.error(globalMessage);
      }
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-background"
      data-testid="authentication-update-password-root"
    >
      <div
        className="flex flex-col w-100 gap-6 p-8 bg-card border border-border rounded-lg"
        data-testid="authentication-update-password-card"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="w-42.5 text-foreground">
            <OiWordmarkLogin />
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 pt-4"
            noValidate
            data-testid="authentication-update-password-form"
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className="flex items-center gap-2"
                    data-testid="authentication-update-password-label-password"
                  >
                    New Password
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder="Enter password"
                      autoComplete="new-password"
                      data-testid="authentication-update-password-input-password"
                    />
                  </FormControl>
                  <FormMessage data-testid="authentication-update-password-error-password" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className="flex items-center gap-2"
                    data-testid="authentication-update-password-label-confirm-password"
                  >
                    Confirm Password
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder="Confirm password"
                      data-testid="authentication-update-password-input-confirm-password"
                    />
                  </FormControl>
                  <FormMessage data-testid="authentication-update-password-error-confirm-password" />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-2">
              <GenericButton
                label="Cancel"
                variant="outline"
                type="button"
                onClick={() => void navigate(urls.login.path)}
                data-testid="authentication-update-password-cancel-button"
              />

              <GenericButton
                label="Update"
                type="submit"
                isLoading={isLoading}
                data-testid="authentication-update-password-update-button"
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
